import { FJViewCanvasSingleton, INIT_IMAGE_PADDING, OFFSET_TOP } from '../js/FJViewCanvas';
import FJCreationEditPanel from '../view/FJCreationEditPanel';
import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FJActionBrushEditCommon from '../../../../redux/action/FJActionBrushEditCommon';
import { setSliceDisableReset, setSliceEditImageLoadStatus } from '../../../../redux/slices/FJSliceBrushEditCommon';
import { useMemo } from 'react';
import FJConstant from '../../../../constants/FJConstant';
import PropTypes from 'prop-types';
import { memo } from 'react';
import useLocalStore from '../../../../hooks/useLocalStore';
import FJConfig from '../../../../config/FJConfig';
import FJAppConstant from '../../../../constants/FJAppConstant';

const FJCreationEditPanelContainer = memo(function FJCreationEditPanelContainer({
    activeHeaderTabIndex,
    initImagePadding = INIT_IMAGE_PADDING,
    offsetTop = OFFSET_TOP,
}) {
    useLocalStore();
    const dispatch = useDispatch();

    const editImage = useSelector(state => state.brushEditCommon.editImage);
    const brushSize = useSelector(state => state.brushEditCommon.brushSize);
    const curBrushType = useSelector(state => state.brushEditCommon.curBrushType);
    const isMouseMove = useSelector(state => state.brushEditCommon.isMouseMove);
    const zoom = useSelector(state => state.brushEditCommon.zoom);
    const taskStatus = useSelector(state => state.common.taskStatus);
    const headerTabIndex = useSelector(state => state.common.headerTabIndex);
    const isShowBrushInCenter = useSelector(state => state.brushEditCommon.isShowBrushInCenter);
    const isSlideCompare = useSelector(state => state.brushEditCommon.isSlideCompare);
    const aiMaskActive = useSelector(state => state.brushEditCommon.aiMaskActive);
    const brushEditStatus = useSelector(state => state.brushEditCommon.brushEditStatus);
    const editImageLoadStatus = useSelector(state => state.brushEditCommon.editImageLoadStatus);

    // 编辑的图是否存在
    const isInEditImage = useMemo(() => {
        return !!editImage;
    }, [editImage]);

    const isShowEditCanvas = useMemo(() => {
        return (
            editImageLoadStatus === FJConstant.SUCCESS &&
            taskStatus === FJConstant.DEFAULT &&
            brushEditStatus === FJConstant.DEFAULT &&
            isInEditImage &&
            headerTabIndex === activeHeaderTabIndex
        );
    }, [taskStatus, headerTabIndex, activeHeaderTabIndex, editImageLoadStatus, isInEditImage, brushEditStatus]);

    useEffect(() => {
        FJViewCanvasSingleton.mouseEventType = curBrushType;

        if (isShowEditCanvas) {
            FJViewCanvasSingleton.draw();
        }
    }, [curBrushType, isShowEditCanvas]);

    useEffect(() => {
        FJViewCanvasSingleton.isMove = isMouseMove;
    }, [isMouseMove]);

    useEffect(() => {
        FJViewCanvasSingleton.setBrushSize(brushSize);
    }, [brushSize]);

    useEffect(() => {
        FJViewCanvasSingleton.setAiMaskActive(aiMaskActive);
    }, [aiMaskActive]);

    useEffect(() => {
        FJViewCanvasSingleton.setInitImagePadding(initImagePadding);
        if (isShowEditCanvas) {
            FJViewCanvasSingleton.initRect();
            FJViewCanvasSingleton.draw();
        }
    }, [initImagePadding, isShowEditCanvas]);

    useEffect(() => {
        FJViewCanvasSingleton.setOffsetTop(offsetTop);
        if (isShowEditCanvas) {
            FJViewCanvasSingleton.initRect();
            FJViewCanvasSingleton.draw();
        }
    }, [offsetTop, isShowEditCanvas]);

    const _setZoom = useCallback(
        (zoom, e, isFitClick = false) => {
            FJViewCanvasSingleton.setZoom(zoom, e, { isFitClick });
            dispatch(FJActionBrushEditCommon.updateZoomAction());
            dispatch(FJActionBrushEditCommon.updateEditImageRectAction());
        },
        [dispatch],
    );

    const initViewCanvas = useCallback(
        async (canvas, viewSize) => {
            dispatch(setSliceEditImageLoadStatus(FJConstant.LOADING));
            let loadSuccess = false;
            const hasEditImage = editImage && editImage.src !== '';
            try {
                if (hasEditImage) {
                    await FJViewCanvasSingleton.init(canvas, editImage.src, viewSize);
                    loadSuccess = true;
                    dispatch(setSliceEditImageLoadStatus(FJConstant.SUCCESS));
                }
            } catch (e) {
                loadSuccess = false;
                dispatch(setSliceEditImageLoadStatus(FJConstant.ERROR));
            }

            dispatch(FJActionBrushEditCommon.editImageLoadedAfterAction(loadSuccess));
        },
        [editImage, dispatch],
    );

    const mouseDownHandler = useCallback(
        (e, mouseInToolPanel) => {
            const isUpdateView = FJViewCanvasSingleton.mouseDownHandler(e, mouseInToolPanel);

            if (isUpdateView) {
                dispatch(setSliceDisableReset(false));
                dispatch(FJActionBrushEditCommon.updateBrushUndoRedoAction());
            }
        },
        [dispatch],
    );

    const mouseUpHandler = useCallback(
        (e, mouseInToolPanel) => {
            const isUpdateView = FJViewCanvasSingleton.mouseUpHandler(e, { mouseInToolPanel });
            dispatch(FJActionBrushEditCommon.updateDisableFitButtonAction());
            if (isUpdateView) {
                dispatch(setSliceDisableReset(false));
                dispatch(FJActionBrushEditCommon.updateBrushUndoRedoAction());
            }
        },
        [dispatch],
    );

    const mouseMoveHandler = useCallback(
        (e, mouseInToolPanel) => {
            FJViewCanvasSingleton.mouseMoveHandler(e, mouseInToolPanel);
            if (isSlideCompare && isMouseMove) {
                dispatch(FJActionBrushEditCommon.updateEditImageRectAction());
            }
        },
        [dispatch, isSlideCompare, isMouseMove],
    );

    const mouseEnterHandler = useCallback(() => {
        if (!isShowEditCanvas) return;
    }, [isShowEditCanvas]);

    const mouseLeaveHandler = useCallback(() => {
        if (!isShowEditCanvas) return;
        FJViewCanvasSingleton.mouseLeaveHandler();
    }, [isShowEditCanvas]);

    const bgColor = useMemo(() => {
        if (FJConfig.appType === FJAppConstant.PHOTO_REMOVE_BG || FJConfig.appType === FJAppConstant.OBJECT_ERASER)
            return '#F8F9FB';

        if (FJConfig.appType === FJAppConstant.PHOTO_RESTORATION || FJConfig.appType === FJAppConstant.PHOTO_COLORIZE) {
            return isInEditImage && taskStatus === FJConstant.DEFAULT ? '#f3f3f7' : '#fff';
        }

        return '#f3f3f7';
    }, [isInEditImage, taskStatus]);

    return (
        <FJCreationEditPanel
            initViewCanvas={initViewCanvas}
            mouseDownHandler={mouseDownHandler}
            mouseUpHandler={mouseUpHandler}
            mouseMoveHandler={mouseMoveHandler}
            mouseEnterHandler={mouseEnterHandler}
            mouseLeaveHandler={mouseLeaveHandler}
            setZoom={_setZoom}
            zoom={zoom}
            bgColor={bgColor}
            curBrushType={curBrushType}
            isMouseMove={isMouseMove}
            brushSize={brushSize}
            isShowBrushInCenter={isShowBrushInCenter}
            isShowEditCanvas={isShowEditCanvas}
            activeHeaderTabIndex={activeHeaderTabIndex}
            isShowFloatTool={isInEditImage}
            isActive={activeHeaderTabIndex === headerTabIndex}
            isSlideCompare={isSlideCompare}
            initImagePadding={initImagePadding}
            offsetTop={offsetTop}
            editImageLoadedStatus={editImageLoadStatus}
        />
    );
});

FJCreationEditPanelContainer.propTypes = {
    activeHeaderTabIndex: PropTypes.number,
    initImagePadding: PropTypes.object,
    offsetTop: PropTypes.number,
    isShowThumbnail: PropTypes.bool,
};

export default FJCreationEditPanelContainer;
